import { defineStore } from 'pinia'
import { sendWebSocketMessage } from '@/composables/chat/useWebSocket'
import { useUsersStore } from '@/stores/users'
import { useChatMessages } from '@/stores/chat/messages'
import { useChatAttendance } from '@/stores/chat/attendances'
import { newAttendance } from '@/composables/chat/actions/attendances'
import { telephonyOmnichannelService } from '@/services/telephonyOmnichannel'
import { preWarmAudio, stopAudio, startAudio } from '@/composables/telephony/omnivoip'

export const useTelephonyOmnichannelStore = defineStore('telephonyOmnichannel', {
    state: () => ({
        currentCall: null,
        callStatus: 'idle', // idle | ringing | answered | ended | calling
        callStartTime: null,
        callDuration: '00:00',
        _timerInterval: null,
    }),

    getters: {
        isRinging: (state) => state.callStatus === 'ringing',
        isInCall: (state) => state.callStatus === 'answered',
        isCalling: (state) => state.callStatus === 'calling',
        isIdle: (state) => state.callStatus === 'idle',
        hasActiveCall: (state) => ['ringing', 'answered', 'calling'].includes(state.callStatus),
        clientName: (state) => state.currentCall?.client?.name || 'Desconhecido',
        clientPhoto: (state) => state.currentCall?.client?.photo || null,
        clientPhone: (state) => state.currentCall?.client?.user || '',
        channelName: (state) => state.currentCall?.channel?.name || '',
        callUuid: (state) => state.currentCall?.uuid || null,
        attendanceId: (state) => state.currentCall?.attendance?.id || null,
    },

    actions: {
        async startCall(channelId, phoneNumber, { attendanceId = null, clientName = null, serverType = null, serverUrl = null, apiId = null } = {}) {
            if (this.hasActiveCall) {
                console.warn('[TelephonyOmnichannel] Already in a call')
                return
            }
            if (!attendanceId) {
                console.warn('[TelephonyOmnichannel] attendanceId is required to make a call')
                return
            }

            if (serverType) preWarmAudio(serverType)

            this.callStatus = 'calling'
            this.currentCall = {
                channel: { id: channelId },
                client: { name: clientName || phoneNumber, user: phoneNumber },
                attendance: { id: attendanceId },
                serverType,
                serverUrl,
                apiId,
                uuid: null,
            }

            try {
                const result = await telephonyOmnichannelService.makeCall(channelId, attendanceId, phoneNumber)
                const callData = result?.data || result
                if (callData) {
                    this.currentCall.callId = callData.call_id
                    if (callData.server_type) this.currentCall.serverType = callData.server_type
                    if (callData.server_url) this.currentCall.serverUrl = callData.server_url
                    if (callData.api_id) this.currentCall.apiId = callData.api_id
                }
            } catch (e) {
                console.error('[TelephonyOmnichannel] makeCall API error:', e)
                this.resetCall()
            }
        },

        setOutgoingCall({ channel, client, attendance, callId, serverType, serverUrl, apiId }) {
            if (this.hasActiveCall) {
                console.warn('[TelephonyOmnichannel] Already in a call')
                return
            }

            if (serverType) preWarmAudio(serverType)

            this.callStatus = 'calling'
            this.currentCall = {
                channel,
                client,
                attendance,
                callId,
                serverType,
                serverUrl,
                apiId,
                uuid: callId,
            }

            if (serverType && serverUrl && callId) {
                setTimeout(() => {
                    if (this.hasActiveCall && this.currentCall?.callId === callId) {
                        startAudio(serverType, serverUrl, apiId, callId)
                    }
                }, 1500)
            }
        },

        setIncomingCall(data) {
            if (this.hasActiveCall) {
                console.warn('[TelephonyOmnichannel] Already in a call, ignoring new_call')
                return
            }

            const attendance = data.attendance || {}
            const client = data.client || { name: 'Desconhecido', user: '', tags: [], photo: null }

            if (!attendance.client) attendance.client = client
            if (!attendance.client.tags) attendance.client.tags = []
            if (!attendance.transfered_to) attendance.transfered_to = []
            if (!attendance.last_message) {
                attendance.last_message = { timestamp: Date.now(), content: '' }
            }

            this.currentCall = {
                channel: data.channel,
                client: client,
                uuid: data.uuid,
                call: data.call,
                callId: data.call?.call_id || data.uuid,
                timestamp: data.timestamp,
                integration_type: data.integration_type,
                serverType: data.channel?.server_type || data.integration_type,
                serverUrl: data.channel?.server_url,
                apiId: data.channel?.server_id,
                extension: data.extension,
                agent: data.agent,
                queue: data.queue,
                raw_event_type: data.raw_event_type,
                attendance: attendance,
            }
            this.callStatus = 'ringing'
        },

        async acceptCall() {
            if (this.callStatus !== 'ringing' || !this.currentCall) return

            if (this.currentCall.serverType) preWarmAudio(this.currentCall.serverType)

            const channelId = this.currentCall.channel?.id
            const attendanceId = this.currentCall.attendance?.id
            const callId = this.currentCall.callId || this.currentCall.call?.uuid || this.currentCall.uuid

            this.callStatus = 'answered'
            this._startTimer()

            const attendance = this.currentCall.attendance
            if (attendance?.id) {
                const me = useUsersStore().me
                const messagesStore = useChatMessages()()
                const attendanceStore = useChatAttendance()()

                const needsAssign = !(attendance.chat_step === 'progress' && attendance.user_id === me.id)
                if (needsAssign) {
                    sendWebSocketMessage({
                        type: 'chat',
                        action: 'assign',
                        data: {
                            attendance_id: attendance.id,
                            user_id: me.id,
                            ignore_automatic_message: true,
                        },
                    })
                    attendance.chat_step = 'progress'
                    attendance.user_id = me.id
                    newAttendance(attendance, true, 'acceptOmnichannelCall')
                }

                messagesStore.requestMessages(attendance.id)
                attendanceStore.setCurrentAttendance(attendance.id)
                this.$router.push({
                    name: 'chat.attendances.progress',
                    params: { id: attendance.id },
                })
            }

            telephonyOmnichannelService.acceptCall(channelId, attendanceId, callId)
                .then((result) => {
                    const respData = result?.data || result
                    if (respData?.server_url) {
                        this.currentCall.serverUrl = respData.server_url
                        this.currentCall.serverType = respData.server_type || this.currentCall.serverType
                    }
                    if (respData?.call_id) this.currentCall.callId = respData.call_id
                    if (respData?.api_id) this.currentCall.apiId = respData.api_id
                })
                .catch((e) => {
                    console.error('[TelephonyOmnichannel] acceptCall API error:', e?.response?.data || e)
                })
        },

        async rejectCall() {
            if (!this.hasActiveCall || !this.currentCall) return

            stopAudio()

            const channelId = this.currentCall.channel?.id
            const attendanceId = this.currentCall.attendance?.id
            const callId = this.currentCall.callId || this.currentCall.call?.uuid || this.currentCall.uuid

            this.callStatus = 'ended'
            this._stopTimer()

            try {
                await telephonyOmnichannelService.rejectCall(channelId, attendanceId, callId)
            } catch (e) {
                console.error('[TelephonyOmnichannel] rejectCall API error:', e)
            }
        },

        async endCall() {
            if (!this.hasActiveCall || !this.currentCall) return

            stopAudio()

            const channelId = this.currentCall.channel?.id
            const attendanceId = this.currentCall.attendance?.id
            const callId = this.currentCall.callId || this.currentCall.call?.uuid || this.currentCall.uuid

            this.callStatus = 'ended'
            this._stopTimer()

            try {
                await telephonyOmnichannelService.endCall(channelId, attendanceId, callId)
            } catch (e) {
                console.error('[TelephonyOmnichannel] endCall API error:', e?.response?.data || e)
            }
        },

        resetCall() {
            stopAudio()
            this._stopTimer()
            this.currentCall = null
            this.callStatus = 'idle'
            this.callStartTime = null
            this.callDuration = '00:00'
        },

        _startTimer() {
            this.callStartTime = Date.now()
            this._timerInterval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - this.callStartTime) / 1000)
                const mins = Math.floor(elapsed / 60).toString().padStart(2, '0')
                const secs = (elapsed % 60).toString().padStart(2, '0')
                this.callDuration = `${mins}:${secs}`
            }, 1000)
        },

        _stopTimer() {
            if (this._timerInterval) {
                clearInterval(this._timerInterval)
                this._timerInterval = null
            }
        },
    },
})
